import { Component, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { TranslationService } from './core/services/translation.service';
import { NavbarComponent } from './shared/components/navbar/navbar.component';
import { FooterComponent } from './shared/components/footer/footer.component';
import { environment } from '../environments/environment';
import { FirestoreConnectionService } from './core/services/firestore/firestore-connection.service';
import {
  getFirebaseConfigInfo,
  validatePubSubConfig,
} from '../environments/firebase.config';
import { GooglePubSubService } from './core/services/pubsub/google-pubsub.service';
import { AgriFlowMessagingService } from './core/services/pubsub/agriflow-messaging.service';
import { NotificationService } from './core/services/notifications/notification.service';
import { AuthService } from './core/services/auth.service';
import { UserService } from './core/services/user.service';

import { ToastModule } from 'primeng/toast';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, NavbarComponent, FooterComponent, ToastModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  title = 'AgriFlow';
  environmentName = environment.name || 'UNKNOWN';
  apiUrl = environment.apiUrl || 'NOT SET';
  isProduction = environment.production;

  constructor(
    private logger: NGXLogger,
    private translationService: TranslationService,
    private firestoreConnectionService: FirestoreConnectionService,
    private pubsubService: GooglePubSubService,
    private messagingService: AgriFlowMessagingService,
    private notificationService: NotificationService,
    private authService: AuthService,
    private userService: UserService,
  ) {}

  async ngOnInit() {
    // Mostra banner di benvenuto
    this.printWelcomeBanner();

    // Log informazioni ambiente
    /* this.logger.info(
      '[APP] AgriFlow avviato con successo in ambiente: ' +
        this.environmentName +
        " usando le API all'url: " +
        this.apiUrl,
    ); */

    // Log configurazione Firebase
    const firebaseConfig = getFirebaseConfigInfo();
    /* this.logger.info('[APP] Configurazione Firebase:', {
      projectId: firebaseConfig.projectId,
      environment: firebaseConfig.environment,
      configValid: firebaseConfig.isValid
    }); */

    // Test connessioni Firebase (Firestore + Storage)
    try {
      const connectionResults =
        await this.firestoreConnectionService.testAllConnections();

      if (connectionResults.overall.connected) {
        this.logger.info(
          '[APP] ✅ Connessioni Firebase stabilite con successo!',
          {
            serviziConnessi: connectionResults.overall.services,
            firestore: connectionResults.firestore,
            storage: connectionResults.storage,
          },
        );
      } else {
        this.logger.warn('[APP] ⚠️ Problemi con le connessioni Firebase:', {
          serviziConnessi: connectionResults.overall.services,
          firestore: connectionResults.firestore,
          storage: connectionResults.storage,
        });
      }
    } catch (error) {
      this.logger.error(
        '[APP] ❌ Errore durante il test delle connessioni Firebase:',
        error,
      );
    }

    // Inizializza le traduzioni italiane per PrimeNG
    this.translationService.setItalianTranslations();
    //this.logger.info('Traduzioni italiane caricate per PrimeNG');

    // Inizializza le notifiche PEC quando l'utente è autenticato (se abilitato)
    if (environment.features?.pecNotifications) {
      this.initializePecNotifications();
    } else {
      this.logger.info(
        '[APP] ℹ️ Notifiche PEC disabilitate nella configurazione',
      );
    }
  }

  /**
   * Inizializza il servizio di notifiche PEC
   */
  private initializePecNotifications(): void {
    this.authService.isAuthenticated$.subscribe((isAuthenticated) => {
      if (isAuthenticated) {
        // Attendi che i dati utente siano caricati
        setTimeout(() => {
          const user = this.userService.getUser();
          if (user && user.id) {
            const userId = user.id.toString();
            //  this.logger.info('[APP] Connessione notifiche PEC per utente:', userId);
            this.notificationService.connect(userId);
          } else {
            // Fallback: prova con localStorage
            const userIdFromStorage = localStorage.getItem('id');
            if (userIdFromStorage) {
              //  this.logger.info('[APP] Connessione notifiche PEC per utente (da storage):', userIdFromStorage);
              this.notificationService.connect(userIdFromStorage);
            } else {
              this.logger.warn(
                '[APP] Impossibile inizializzare notifiche PEC: user ID non disponibile',
              );
            }
          }
        }, 1000); // Attendi 1 secondo per il caricamento dei dati utente
      } else {
        // Disconnetti quando l'utente fa logout
        this.notificationService.disconnect();
      }
    });
  }

  /**
   * Mostra il banner di benvenuto ASCII art nella console
   */
  private printWelcomeBanner(): void {
    const version = environment.version || '0.0.0';
    const year = new Date().getFullYear();

    // Colori ANSI per la console
    const colors = {
      reset: '\x1b[0m',
      bright: '\x1b[1m',
      green: '\x1b[32m',
      cyan: '\x1b[36m',
      yellow: '\x1b[33m',
      blue: '\x1b[34m',
      magenta: '\x1b[35m',
    };

    const banner = `
${colors.bright}${colors.green}
   ___   ___________  ________    ____ _       __
  / _ | / ___/ __/ / / / __/ /   / __ \\ |     / /
 / __ |/ (_ / _// /_/ / _// /__ / /_/ / | /| / /
/_/ |_|\\___/_/ /____ /_/ /____/ \\____/|_|/ |_|/
${colors.reset}
${colors.bright}${colors.cyan}╔════════════════════════════════════════════════════╗
║                                                    ║
║     🌾  BENVENUTO IN AGRIFLOW  🌾                  ║
║                                                    ║
║     Sistema di Gestione Agricola                  ║
║                                                    ║
╚════════════════════════════════════════════════════╝${colors.reset}

${colors.yellow}📦 Versione:${colors.reset}      ${version}
${colors.blue}🌍 Ambiente:${colors.reset}      ${this.environmentName}
${colors.magenta}🔗 API URL:${colors.reset}       ${this.apiUrl}
${colors.green}📅 Anno:${colors.reset}          ${year}

${colors.bright}Powered by Angular 19 & PrimeNG${colors.reset}
`;

    console.log(banner);

    // Log aggiuntivo per sviluppatori
    if (!this.isProduction) {
      console.log(
        `%c💡 Modalità Sviluppo Attiva`,
        'background: #ff9800; color: white; padding: 4px 8px; border-radius: 3px; font-weight: bold;',
      );
      console.log(
        `%cℹ️ Console DevTools disponibile per debugging`,
        'color: #2196F3; font-size: 12px;',
      );
    }
  }
}
